import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Select from 'react-select';
import TourExpenseClaimController from '../Controller/TourExpenseClaimController';
import Loader from 'utils/Loader';
import { showAlert } from 'utils/ErrorMessage';
import { showSuccessAlert } from 'utils/SuccessMessage';
import { BASE_URL, getAuthHeaders, getAgentTourExpenseList, getTourExpenseClaimHistory, insertTourExpenseClaim, updateClaimApprovalStatus } from 'Service/ApiService';

const allowedFileTypes = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const allowedFileExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'];

const selectFieldProps = {
  SelectProps: {
    IconComponent: KeyboardArrowDownIcon,
    displayEmpty: true
  },
  InputLabelProps: {
    shrink: true
  },
  sx: {
    '& .MuiFormLabel-asterisk': {
      color: '#d32f2f'
    },
    '& .MuiSelect-icon': {
      display: 'block',
      color: 'rgba(0, 0, 0, 0.54)'
    }
  }
};

const textFieldProps = {
  InputLabelProps: {
    shrink: true
  },
  sx: {
    '& .MuiFormLabel-asterisk': {
      color: '#d32f2f'
    }
  }
};

const searchableDropdownStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 4,
    borderColor: state.isFocused ? '#1976d2' : 'rgba(0, 0, 0, 0.23)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(0, 0, 0, 0.87)'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    minHeight: 40,
    padding: '0 14px'
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.6)'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.54)',
    paddingRight: 12
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1300
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1600
  })
};

const selectMenuPortalTarget = typeof document !== 'undefined' ? document.body : null;

const searchableFieldWrapperSx = {
  position: 'relative'
};

const searchableFieldLabelSx = {
  position: 'absolute',
  top: -8,
  left: 12,
  px: 0.5,
  backgroundColor: '#fff',
  color: 'rgba(0, 0, 0, 0.6)',
  fontSize: '0.75rem',
  lineHeight: 1,
  zIndex: 1
};

const getSearchableNoOptionsMessage = ({ inputValue }) => {
  if (String(inputValue || '').trim().length < 3) {
    return 'Type at least 3 characters';
  }

  return 'No data found';
};

const formatNameCodeDisplay = (name, code) => {
  const normalizedCode = String(code || '').trim();
  let normalizedName = String(name || '').trim();

  if (normalizedName && normalizedCode) {
    normalizedName = normalizedName
      .replace(new RegExp(`^${normalizedCode}[\\s-]*`, 'i'), '')
      .replace(new RegExp(`[\\s-]*${normalizedCode}$`, 'i'), '')
      .trim();
  }

  return [normalizedName, normalizedCode].filter(Boolean).join(' - ') || '-';
};

const normalizeFilterValue = (value) => String(value || '').trim().toLowerCase();

const compactHeaderCellSx = {
  px: 1,
  py: 1,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  fontSize: '0.72rem',
  fontWeight: 700
};

const compactBodyCellSx = {
  px: 1,
  py: 0.75,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  fontSize: '0.72rem',
  verticalAlign: 'top'
};

const equalCellSx = { width: 'auto' };
const centeredCellSx = { textAlign: 'center' };
const remarksColumnSx = { width: '18%' };

const getStatusTextSx = (status) => {
  const normalizedStatus = String(status || '').toLowerCase();

  if (normalizedStatus === 'approved') {
    return {
      color: '#1b5e20',
      fontWeight: 700
    };
  }

  if (normalizedStatus === 'approval pending' || normalizedStatus === 'pending') {
    return {
      color: '#b26a00',
      fontWeight: 700
    };
  }

  return {
    color: '#455a64',
    fontWeight: 700
  };
};

const isApprovalActionDisabled = (claim, { isSPUser, isSOUser }) => {
  const normalizedStatus = String(claim?.status || '').trim().toLowerCase();
  const isSalesPersonApproved = String(claim?.claimApprovedBySalesPerson || '').trim().toLowerCase() === 'yes';
  const isSalesOfficerApproved = String(claim?.claimApprovedBySalesOfficer || '').trim().toLowerCase() === 'yes';

  // if (normalizedStatus === 'approved') {
  //   return true;
  // }

  if (isSPUser) {
    return isSalesPersonApproved;
  }

  if (isSOUser) {
    return !isSalesPersonApproved || isSalesOfficerApproved;
  }

  return false;
};

const parseClaimDate = (value) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    const parsedDate = new Date(`${normalizedValue}T00:00:00`);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalizedValue)) {
    const [day, month, year] = normalizedValue.split('/');
    const parsedDate = new Date(`${year}-${month}-${day}T00:00:00`);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const parsedDate = new Date(normalizedValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDisplayDate = (value) => {
  const parsedDate = parseClaimDate(value);

  if (!parsedDate) {
    return value || '-';
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatHistoryDateTime = (value) => {
  const parsedDate = parseClaimDate(value);

  if (!parsedDate) {
    return value || '-';
  }

  return parsedDate.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatDialogDate = (value) => {
  const parsedDate = parseClaimDate(value);

  if (!parsedDate) {
    return value || '';
  }

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const year = parsedDate.getFullYear();

  return `${day}-${month}-${year}`;
};

const initialForm = {
  fromDate: '',
  toDate: '',
  salespersonCode: '',
  modeOfTransport: '',
  vehicleNumber: '',
  totalKilometersTravelled: '',
  fromStation: '',
  midStations: '',
  toStation: '',
  ratePerKilometer: '',
  totalAmount: '',
  travelingAgency: '',
  salespersonL1Code: '',
  travelAgencyCode: '',
  bankName: '',
  branchName: '',
  accountHolderName: '',
  bankAccountNumber: '',
  ifscCode: '',
  hrOneReferenceNumber: '',
  fullTourFromDate: '',
  fullTourToDate: '',
  additionalRemarks: '',
  invoiceFile: null
};

const reimbursementOptions = [
  { value: '50', label: '50%' },
  { value: '100', label: '100%' }
];

const statusFilterOptions = [
  { value: '', label: 'All' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' }
];

const acceptedInvoiceFormatsLabel = 'Accepted formats: JPG, JPEG, PNG, PDF, DOC, DOCX';
const bankAccountNumberPattern = /^\d{9,18}$/;
const ifscCodePattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const BigIntValue = typeof window !== 'undefined' ? window.BigInt : null;

const sanitizeDecimalInput = (value) => {
  const rawValue = String(value || '');
  const cleanedValue = rawValue.replace(/[^\d.]/g, '');

  if (!cleanedValue) {
    return '';
  }

  const [wholePart, ...decimalParts] = cleanedValue.split('.');
  const normalizedWholePart = wholePart.replace(/^0+(?=\d)/, '') || '0';

  if (decimalParts.length === 0) {
    return normalizedWholePart;
  }

  return `${normalizedWholePart}.${decimalParts.join('')}`;
};

const parseDecimalToScaledInteger = (value) => {
  const normalizedValue = sanitizeDecimalInput(value);

  if (!normalizedValue) {
    return null;
  }

  const [wholePart, decimalPart = ''] = normalizedValue.split('.');
  const digits = `${wholePart}${decimalPart}`.replace(/^0+(?=\d)/, '') || '0';

  return {
    integer: BigIntValue(digits),
    scale: decimalPart.length
  };
};

const formatScaledIntegerToCurrency = (integerValue, scale) => {
  let scaledInteger = integerValue;
  let effectiveScale = scale;

  if (effectiveScale > 2) {
    const roundingDivisor = 10n ** BigIntValue(effectiveScale - 2);
    const quotient = scaledInteger / roundingDivisor;
    const remainder = scaledInteger % roundingDivisor;
    scaledInteger = remainder * 2n >= roundingDivisor ? quotient + 1n : quotient;
    effectiveScale = 2;
  } else if (effectiveScale < 2) {
    scaledInteger *= 10n ** BigIntValue(2 - effectiveScale);
    effectiveScale = 2;
  }

  const isNegative = scaledInteger < 0n;
  const normalizedInteger = isNegative ? -scaledInteger : scaledInteger;
  const rawDigits = normalizedInteger.toString().padStart(effectiveScale + 1, '0');
  const integerPart = rawDigits.slice(0, -effectiveScale) || '0';
  const decimalPart = rawDigits.slice(-effectiveScale);

  return `${isNegative ? '-' : ''}${integerPart}.${decimalPart}`;
};

const calculateTotalAmount = (kilometersValue, rateValue) => {
  if (!BigIntValue) {
    const kilometers = Number(kilometersValue || 0);
    const rate = Number(rateValue || 0);
    return kilometers > 0 && rate > 0 ? (kilometers * rate).toFixed(2) : '';
  }

  const parsedKilometers = parseDecimalToScaledInteger(kilometersValue);
  const parsedRate = parseDecimalToScaledInteger(rateValue);

  if (!parsedKilometers || !parsedRate || parsedKilometers.integer === 0n || parsedRate.integer === 0n) {
    return '';
  }

  const multipliedInteger = parsedKilometers.integer * parsedRate.integer;
  const multipliedScale = parsedKilometers.scale + parsedRate.scale;

  return formatScaledIntegerToCurrency(multipliedInteger, multipliedScale);
};

const getFieldFormatError = (name, value) => {
  const trimmedValue = String(value || '').trim();

  if (!trimmedValue) {
    return '';
  }

  if (name === 'bankAccountNumber' && !bankAccountNumberPattern.test(trimmedValue)) {
    return 'Bank Account Number must be 9 to 18 digits';
  }

  if (name === 'ifscCode' && !ifscCodePattern.test(trimmedValue)) {
    return 'Enter a valid IFSC code like HDFC0001234';
  }

  return '';
};

const TourExpenseClaim = () => {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invoiceFileName, setInvoiceFileName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedApprovalClaim, setSelectedApprovalClaim] = useState(null);
  const [selectedClaimDetails, setSelectedClaimDetails] = useState(null);
  const [selectedHistoryClaim, setSelectedHistoryClaim] = useState(null);
  const [claimHistory, setClaimHistory] = useState([]);
  const [claimHistoryLoading, setClaimHistoryLoading] = useState(false);
  const [claimHistoryError, setClaimHistoryError] = useState('');
  const [travelAgencyOptions, setTravelAgencyOptions] = useState([]);
  const [travelAgencyLoading, setTravelAgencyLoading] = useState(false);
  const [travelAgencySearchText, setTravelAgencySearchText] = useState('');
  const [agentFilters, setAgentFilters] = useState({
    salespersonCode: '',
    salespersonL1Code: '',
    travelingAgency: '',
    status: '',
    hrOneReferenceNumber: '',
    fromDate: '',
    toDate: ''
  });
  const [selectedSalespersonOption, setSelectedSalespersonOption] = useState(null);
  const [selectedSalespersonL1Option, setSelectedSalespersonL1Option] = useState(null);
  const [selectedTravelAgencyOption, setSelectedTravelAgencyOption] = useState(null);
  const [selectedAgentFilterSalespersonOption, setSelectedAgentFilterSalespersonOption] = useState(null);
  const [selectedAgentFilterSalespersonL1Option, setSelectedAgentFilterSalespersonL1Option] = useState(null);
  const [selectedAgentFilterTravelAgencyOption, setSelectedAgentFilterTravelAgencyOption] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    reimbursementPercentage: '',
    additionalRemarks: ''
  });
  const [approvalErrors, setApprovalErrors] = useState({});
  const [approvalSubmitError, setApprovalSubmitError] = useState('');
  const [isApprovalSubmitting, setIsApprovalSubmitting] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [claimListRefreshKey, setClaimListRefreshKey] = useState(0);
  const {
    claims,
    isAgentUser,
    isPrimUser,
    isSOUser,
    isSPUser,
    isApprovalUser,
    loggedInUserCode,
    salespersonCodeOptions,
    salespersonL1Options,
    salespersonSearchText,
    salespersonL1SearchText,
    setClaims,
    setSalespersonL1SearchText,
    setSalespersonSearchText,
    visibleClaims,
  } = TourExpenseClaimController();

  useEffect(() => {
    let isActive = true;
    const trimmedSearchText = travelAgencySearchText.trim();

    const fetchTravelAgencyList = async () => {
      if (!isAgentUser && !isApprovalUser) {
        if (isActive) {
          setTravelAgencyOptions([]);
          setTravelAgencyLoading(false);
        }
        return;
      }

      if (trimmedSearchText && trimmedSearchText.length < 3) {
        if (isActive) {
          setTravelAgencyLoading(false);
        }
        return;
      }

      try {
        setTravelAgencyLoading(true);
        const formDataToSend = new FormData();
        formDataToSend.append('searchUsingCodeName', trimmedSearchText);

        const response = await fetch(BASE_URL + 'tourexpenseclaim/travelAgencyList.php', {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...getAuthHeaders()
          },
          body: formDataToSend
        });

        const data = await response.json();

        if (!isActive) {
          return;
        }

        if (data.success === 'true' && data.travelAgencyList) {
          const formattedOptions = data.travelAgencyList.map((agency) => ({
            value: agency.supplier,
            label: formatNameCodeDisplay(agency.name, agency.supplier),
            agencyCode: agency.supplier,
            agencyName: agency.name || '',
            bankAccountNumber: agency.bankAccountNumber || '',
            ifscCode: agency.ifscCode || ''
          }));
          setTravelAgencyOptions(formattedOptions);
        } else {
          setTravelAgencyOptions([]);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error('Error fetching travel agency list:', error);
        setTravelAgencyOptions([]);
        showAlert('Error fetching travel agency list. Please try again.');
      } finally {
        if (isActive) {
          setTravelAgencyLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(fetchTravelAgencyList, 300);

    return () => {
      isActive = false;
      clearTimeout(debounceTimer);
    };
  }, [isAgentUser, isApprovalUser, travelAgencySearchText]);

  useEffect(() => {
    const calculatedAmount = calculateTotalAmount(
      formData.totalKilometersTravelled,
      formData.ratePerKilometer
    );

    setFormData((prev) => (
      prev.totalAmount === calculatedAmount
        ? prev
        : { ...prev, totalAmount: calculatedAmount }
    ));
  }, [formData.totalKilometersTravelled, formData.ratePerKilometer]);

  const selectedTravelAgencyDetails = useMemo(
    () => {
      if (!formData.travelingAgency) {
        return null;
      }

      return travelAgencyOptions.find((option) => option.value === formData.travelingAgency) || selectedTravelAgencyOption || null;
    },
    [formData.travelingAgency, selectedTravelAgencyOption, travelAgencyOptions]
  );

  const resolvedSalespersonOption = useMemo(
    () => salespersonCodeOptions.find((option) => option.value === formData.salespersonCode)
      || selectedSalespersonOption
      || null,
    [formData.salespersonCode, selectedSalespersonOption, salespersonCodeOptions]
  );

  const resolvedSalespersonL1Option = useMemo(
    () => salespersonL1Options.find((option) => option.value === formData.salespersonL1Code)
      || selectedSalespersonL1Option
      || null,
    [formData.salespersonL1Code, selectedSalespersonL1Option, salespersonL1Options]
  );

  const resolvedAgentFilterSalespersonOption = useMemo(
    () => salespersonCodeOptions.find((option) => option.value === agentFilters.salespersonCode)
      || selectedAgentFilterSalespersonOption
      || null,
    [agentFilters.salespersonCode, selectedAgentFilterSalespersonOption, salespersonCodeOptions]
  );

  const resolvedAgentFilterSalespersonL1Option = useMemo(
    () => salespersonL1Options.find((option) => option.value === agentFilters.salespersonL1Code)
      || selectedAgentFilterSalespersonL1Option
      || null,
    [agentFilters.salespersonL1Code, selectedAgentFilterSalespersonL1Option, salespersonL1Options]
  );

  const resolvedAgentFilterTravelAgencyOption = useMemo(
    () => travelAgencyOptions.find((option) => option.value === agentFilters.travelingAgency)
      || selectedAgentFilterTravelAgencyOption
      || null,
    [agentFilters.travelingAgency, selectedAgentFilterTravelAgencyOption, travelAgencyOptions]
  );

  const filteredAgentClaims = useMemo(() => {
    if (!isAgentUser && !isApprovalUser) {
      return visibleClaims;
    }

    return visibleClaims.filter((claim) => {
      const matchesSalesperson = !agentFilters.salespersonCode || claim.salespersonCode === agentFilters.salespersonCode;
      const matchesSalespersonL1 = !agentFilters.salespersonL1Code || claim.salespersonL1Code === agentFilters.salespersonL1Code;
      const selectedTravelAgency = normalizeFilterValue(agentFilters.travelingAgency);
      const matchesTravelAgency = !selectedTravelAgency
        || [
          claim.travelAgencyCode,
          claim.travelingAgency,
          claim.travelAgencyName
        ].some((value) => normalizeFilterValue(value) === selectedTravelAgency);
      const matchesStatus = !agentFilters.status
        || String(claim.status || '').toLowerCase() === String(agentFilters.status || '').toLowerCase();
      const matchesHrProgram = !agentFilters.hrOneReferenceNumber
        || String(claim.hrOneReferenceNumber || '').toLowerCase().includes(agentFilters.hrOneReferenceNumber.toLowerCase());

      const claimFromDate = parseClaimDate(claim.fromDate);
      const claimToDate = parseClaimDate(claim.toDate);
      const filterFromDate = agentFilters.fromDate ? new Date(`${agentFilters.fromDate}T00:00:00`) : null;
      const filterToDate = agentFilters.toDate ? new Date(`${agentFilters.toDate}T00:00:00`) : null;

      const matchesFromDate = !filterFromDate || (claimFromDate && claimFromDate >= filterFromDate);
      const matchesToDate = !filterToDate || (claimToDate && claimToDate <= filterToDate);

      return matchesSalesperson && matchesSalespersonL1 && matchesTravelAgency && matchesStatus && matchesHrProgram && matchesFromDate && matchesToDate;
    });
  }, [agentFilters, isAgentUser, isApprovalUser, visibleClaims]);

  useEffect(() => {
    let isActive = true;

    const loadAgentClaims = async () => {
      if ((!isAgentUser && !isApprovalUser) || showCreateForm) {
        return;
      }

      try {
        if (isActive) {
          setIsTableLoading(true);
        }
        const response = await getAgentTourExpenseList(agentFilters);

        if (!isActive) {
          return;
        }

        const nextClaims = Array.isArray(response?.data)
          ? response.data.map((claim) => {
            const claimApprovedBySalesPerson = claim.claimApprovedBySalesPerson || '';
            const claimApprovedBySalesOfficer = claim.claimApprovedBySalesOfficer || '';
            const shouldShowPendingForSO = isSOUser
              && String(claimApprovedBySalesPerson).trim().toLowerCase() === 'yes'
              && String(claimApprovedBySalesOfficer).trim().toLowerCase() === 'no';
            const derivedApprovalStatus = shouldShowPendingForSO
              ? 'Pending'
              : (claim.approvalStatus || '');

            return ({
              claimID: claim.claimID,
              id: claim.showId || claim.documentNo || `TEC-${claim.claimID}`,
              documentNo: claim.documentNo || '',
              agencyCode: claim.agencyCode || '',
              fromDate: claim.fromDate || '',
              toDate: claim.toDate || '',
              salespersonCode: claim.salespersonCode || '',
              salespersonName: claim.salespersonName || '',
              salespersonL1Code: claim.salesOfficerCodeL1 || '',
              salesOfficerName: claim.salesOfficerName || '',
              modeOfTransport: claim.modeOfTransport || '',
              vehicleNumber: claim.vehicleNumber || '',
              totalKilometersTravelled: claim.totalKilometers || '',
              fromStation: claim.fromStation || '',
              midStations: claim.midStations || '',
              toStation: claim.toStation || '',
              ratePerKilometer: claim.ratePerKilometer || '',
              totalAmount: claim.totalAmount || '',
              amount: claim.totalAmount || '',
              invoiceFileName: claim.invoiceFileName || '',
              invoiceFilePath: claim.invoiceFilePath || `${BASE_URL}uploads/tour_expenses/${claim.invoiceFileName || ''}`,
              travelAgencyName: claim.travelAgencyName || '',
              travelingAgency: claim.travelAgencyName || claim.travelAgencyCode || '',
              travelAgencyCode: claim.travelAgencyCode || '',
              accountHolderName: claim.accountHolderName || '',
              bankName: claim.bankName || '',
              branchName: claim.branchName || '',
              bankAccountNumber: claim.bankAccountNumber || '',
              ifscCode: claim.ifscCode || '',
              hrOneReferenceNumber: claim.hrTourProgramReferenceNo || '',
              fullTourFromDate: claim.fullTourFromDate || '',
              fullTourToDate: claim.fullTourToDate || '',
              reimbursementPercentage: claim.reimbursementPercentage || '',
              additionalRemarks: claim.remarks || '',
              remarks: claim.remarks || '',
              status: derivedApprovalStatus,
              approvalStatus: derivedApprovalStatus,
              claimApprovedBySalesPerson,
              claimApprovedBySalesOfficer,
              createdAt: claim.createdAt || ''
            });
          })
          : [];

        setClaims(nextClaims);
      } catch (error) {
        if (isActive) {
          setClaims([]);
          showAlert(error?.message || 'Failed to load tour expense claims.');
        }
      } finally {
        if (isActive) {
          setIsTableLoading(false);
        }
      }
    };

    loadAgentClaims();

    return () => {
      isActive = false;
    };
  }, [agentFilters, claimListRefreshKey, isAgentUser, isApprovalUser, isSOUser, setClaims, showCreateForm]);

  useEffect(() => {
    if (!formData.salespersonCode) {
      setSelectedSalespersonOption(null);
      return;
    }

    const matchedOption = salespersonCodeOptions.find((option) => option.value === formData.salespersonCode);
    if (matchedOption) {
      setSelectedSalespersonOption(matchedOption);
    }
  }, [formData.salespersonCode, salespersonCodeOptions]);

  useEffect(() => {
    if (!formData.salespersonL1Code) {
      setSelectedSalespersonL1Option(null);
      return;
    }

    const matchedOption = salespersonL1Options.find((option) => option.value === formData.salespersonL1Code);
    if (matchedOption) {
      setSelectedSalespersonL1Option(matchedOption);
    }
  }, [formData.salespersonL1Code, salespersonL1Options]);

  useEffect(() => {
    if (!formData.travelingAgency) {
      setSelectedTravelAgencyOption(null);
      return;
    }

    const matchedOption = travelAgencyOptions.find((option) => option.value === formData.travelingAgency);
    if (matchedOption) {
      setSelectedTravelAgencyOption(matchedOption);
    }
  }, [formData.travelingAgency, travelAgencyOptions]);

  useEffect(() => {
    if (!agentFilters.salespersonCode) {
      setSelectedAgentFilterSalespersonOption(null);
      return;
    }

    const matchedOption = salespersonCodeOptions.find((option) => option.value === agentFilters.salespersonCode);
    if (matchedOption) {
      setSelectedAgentFilterSalespersonOption(matchedOption);
    }
  }, [agentFilters.salespersonCode, salespersonCodeOptions]);

  useEffect(() => {
    if (!agentFilters.salespersonL1Code) {
      setSelectedAgentFilterSalespersonL1Option(null);
      return;
    }

    const matchedOption = salespersonL1Options.find((option) => option.value === agentFilters.salespersonL1Code);
    if (matchedOption) {
      setSelectedAgentFilterSalespersonL1Option(matchedOption);
    }
  }, [agentFilters.salespersonL1Code, salespersonL1Options]);

  useEffect(() => {
    if (!agentFilters.travelingAgency) {
      setSelectedAgentFilterTravelAgencyOption(null);
      return;
    }

    const matchedOption = travelAgencyOptions.find((option) => option.value === agentFilters.travelingAgency);
    if (matchedOption) {
      setSelectedAgentFilterTravelAgencyOption(matchedOption);
    }
  }, [agentFilters.travelingAgency, travelAgencyOptions]);

  const handleTextChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === 'vehicleNumber') {
      nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    }

    if (name === 'ifscCode') {
      nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
    }

    if (name === 'bankAccountNumber') {
      nextValue = value.replace(/\D/g, '').slice(0, 18);
    }

    if (name === 'hrOneReferenceNumber') {
      nextValue = value.toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue
    }));

    const formatError = getFieldFormatError(name, nextValue);
    setErrors((prev) => ({
      ...prev,
      [name]: formatError
    }));
  };

  const handleNumberChange = (event) => {
    const { name, value } = event.target;
    const normalizedValue = value === '' ? '' : sanitizeDecimalInput(value);

    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleSalespersonChange = (selectedOption) => {
    setSelectedSalespersonOption(selectedOption || null);
    setSalespersonSearchText('');
    handleSelectChange('salespersonCode', selectedOption?.value || '');
  };

  const handleSalespersonL1Change = (selectedOption) => {
    setSelectedSalespersonL1Option(selectedOption || null);
    setSalespersonL1SearchText('');
    handleSelectChange('salespersonL1Code', selectedOption?.value || '');
  };

  const handleTravelAgencyChange = (selectedOption) => {
    setSelectedTravelAgencyOption(selectedOption || null);
    setTravelAgencySearchText('');
    setFormData((prev) => ({
      ...prev,
      travelingAgency: selectedOption?.value || '',
      travelAgencyCode: '',
      bankName: '',
      branchName: '',
      accountHolderName: '',
      bankAccountNumber: '',
      ifscCode: ''
    }));
    setErrors((prev) => ({
      ...prev,
      travelingAgency: '',
      travelAgencyCode: '',
      bankName: '',
      branchName: '',
      accountHolderName: '',
      bankAccountNumber: '',
      ifscCode: '',
      bankDetails: ''
    }));
  };

  const handleAgentFilterChange = (event) => {
    const { name, value } = event.target;
    setAgentFilters((prev) => ({
      ...prev,
      // [name]: value
      [name]:
        name === 'hrOneReferenceNumber'
          ? value.toUpperCase()
          : value
    }));
  };

  const handleAgentFilterSelectChange = (name, selectedOption) => {
    if (name === 'salespersonCode') {
      setSelectedAgentFilterSalespersonOption(selectedOption || null);
      setSalespersonSearchText('');
    }

    if (name === 'salespersonL1Code') {
      setSelectedAgentFilterSalespersonL1Option(selectedOption || null);
      setSalespersonL1SearchText('');
    }

    if (name === 'travelingAgency') {
      setSelectedAgentFilterTravelAgencyOption(selectedOption || null);
      setTravelAgencySearchText('');
    }

    setAgentFilters((prev) => ({
      ...prev,
      [name]: selectedOption?.value || ''
    }));
  };

  const handleResetAgentFilters = () => {
    setSelectedAgentFilterSalespersonOption(null);
    setSelectedAgentFilterSalespersonL1Option(null);
    setSelectedAgentFilterTravelAgencyOption(null);
    setSalespersonSearchText('');
    setSalespersonL1SearchText('');
    setTravelAgencySearchText('');
    setAgentFilters({
      salespersonCode: '',
      salespersonL1Code: '',
      travelingAgency: '',
      status: '',
      hrOneReferenceNumber: '',
      fromDate: '',
      toDate: ''
    });
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;

    setFormData((prev) => ({
      ...prev,
      invoiceFile: selectedFile
    }));
    setInvoiceFileName(selectedFile?.name || '');

    setErrors((prev) => ({
      ...prev,
      invoiceFile: ''
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.fromDate) nextErrors.fromDate = 'From Date is required';
    if (!formData.toDate) nextErrors.toDate = 'To Date is required';
    if (!formData.salespersonCode) nextErrors.salespersonCode = 'Salesperson Code is required';
    if (!formData.modeOfTransport.trim()) nextErrors.modeOfTransport = 'Mode of Transport is required';
    if (!formData.vehicleNumber.trim()) nextErrors.vehicleNumber = 'Vehicle Number is required';
    if (!formData.totalKilometersTravelled) nextErrors.totalKilometersTravelled = 'Total KM Travelled is required';
    if (!formData.fromStation.trim()) nextErrors.fromStation = 'From Station is required';
    if (!formData.toStation.trim()) nextErrors.toStation = 'To Station is required';
    if (!formData.ratePerKilometer) nextErrors.ratePerKilometer = 'Rate per KM is required';
    if (!formData.totalAmount) nextErrors.totalAmount = 'Total Amount is required';
    if (!formData.travelingAgency) nextErrors.travelingAgency = 'Traveling Agency is required';
    if (!formData.bankName.trim()) nextErrors.bankName = 'Bank Name is required';
    if (!formData.branchName.trim()) nextErrors.branchName = 'Branch Name is required';
    if (!formData.accountHolderName.trim()) nextErrors.accountHolderName = 'Account Holder Name is required';
    if (!formData.bankAccountNumber.trim()) nextErrors.bankAccountNumber = 'Bank Account Number is required';
    if (!formData.ifscCode.trim()) nextErrors.ifscCode = 'IFSC Code is required';
    if (!formData.hrOneReferenceNumber.trim()) nextErrors.hrOneReferenceNumber = 'HR One Tour Program Number is required';
    if (!formData.fullTourFromDate) nextErrors.fullTourFromDate = 'Full Tour From Date is required';
    if (!formData.fullTourToDate) nextErrors.fullTourToDate = 'Full Tour To Date is required';
    if (!formData.invoiceFile) nextErrors.invoiceFile = 'Invoice Upload is required';

    if (formData.fromDate && formData.toDate && formData.toDate < formData.fromDate) {
      nextErrors.toDate = 'To Date cannot be before From Date';
    }

    if (formData.fullTourFromDate && formData.fullTourToDate && formData.fullTourToDate < formData.fullTourFromDate) {
      nextErrors.fullTourToDate = 'Full Tour To Date cannot be before Full Tour From Date';
    }

    const vehicleNumberFormatError = getFieldFormatError('vehicleNumber', formData.vehicleNumber);
    if (vehicleNumberFormatError) nextErrors.vehicleNumber = vehicleNumberFormatError;

    const bankAccountFormatError = getFieldFormatError('bankAccountNumber', formData.bankAccountNumber);
    if (bankAccountFormatError) nextErrors.bankAccountNumber = bankAccountFormatError;

    const ifscCodeFormatError = getFieldFormatError('ifscCode', formData.ifscCode);
    if (ifscCodeFormatError) nextErrors.ifscCode = ifscCodeFormatError;

    const invoiceExtension = String(formData.invoiceFile?.name || '').split('.').pop()?.toLowerCase() || '';
    const isAllowedInvoiceType = allowedFileTypes.some((type) => (
      type.endsWith('/') ? formData.invoiceFile?.type?.startsWith(type) : formData.invoiceFile?.type === type
    ));
    const isAllowedInvoiceExtension = allowedFileExtensions.includes(invoiceExtension);

    if (formData.invoiceFile && !isAllowedInvoiceType && !isAllowedInvoiceExtension) {
      nextErrors.invoiceFile = acceptedInvoiceFormatsLabel;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleReset = () => {
    setFormData(initialForm);
    setErrors({});
    setInvoiceFileName('');
    setSelectedSalespersonOption(null);
    setSelectedSalespersonL1Option(null);
    setSelectedTravelAgencyOption(null);
  };

  const handleOpenApprovalDialog = (claim) => {
    setSelectedApprovalClaim(claim);
    setApprovalForm({
      reimbursementPercentage: claim.reimbursementPercentage || '',
      additionalRemarks: isSOUser ? '' : (claim.additionalRemarks || '')
    });
    setApprovalErrors({});
  };

  const handleOpenClaimDetails = (claim) => {
    setSelectedClaimDetails(claim);
  };

  const handleOpenClaimHistory = async (claim) => {
    setSelectedHistoryClaim(claim);
    setClaimHistory([]);
    setClaimHistoryError('');
    setClaimHistoryLoading(true);

    try {
      const response = await getTourExpenseClaimHistory(claim?.claimID);
      const nextHistory = Array.isArray(response?.data)
        ? response.data.map((item) => ({
          logID: item.logID,
          actionType: item.actionType || '',
          actionByUserCode: item.actionByUserCode || '',
          actionByUserName: item.actionByUserName || '',
          actionByRole: item.actionByRole || '',
          claimStatus: item.claimStatus || '',
          reimbursementPercentage: item.reimbursementPercentage || '',
          remarks: item.remarks || '',
          createdAt: item.createdAt || ''
        }))
        : [];

      setClaimHistory(nextHistory);
    } catch (error) {
      setClaimHistoryError(error?.message || 'Failed to load claim history.');
    } finally {
      setClaimHistoryLoading(false);
    }
  };

  const handleCloseClaimDetails = () => {
    setSelectedClaimDetails(null);
  };

  const handleCloseClaimHistory = () => {
    setSelectedHistoryClaim(null);
    setClaimHistory([]);
    setClaimHistoryError('');
    setClaimHistoryLoading(false);
  };

  const handleDownloadInvoice = (invoiceFile) => {
    if (!invoiceFile) {
      showAlert('Invoice file is not available.');
      return;
    }

    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${BASE_URL}tourexpenseclaim/downloadTourExpenseInvoice.php`;
      form.style.display = 'none';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'file';
      input.value = invoiceFile;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      showAlert(error?.message || 'Failed to download invoice file.');
    }
  };

  const handleCloseApprovalDialog = () => {
    setSelectedApprovalClaim(null);
    setApprovalForm({
      reimbursementPercentage: '',
      additionalRemarks: ''
    });
    setApprovalErrors({});
    setApprovalSubmitError('');
    setIsApprovalSubmitting(false);
  };

  const handleApprovalFieldChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'additionalRemarks' ? value.slice(0, 2000) : value;

    setApprovalForm((prev) => ({
      ...prev,
      [name]: nextValue
    }));

    setApprovalErrors((prev) => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleApprovalSubmit = async () => {
    if (!selectedApprovalClaim) return;

    const nextErrors = {};

    if (!approvalForm.reimbursementPercentage) {
      nextErrors.reimbursementPercentage = 'Please select reimbursed percentage';
    }

    if (!String(approvalForm.additionalRemarks || '').trim()) {
      nextErrors.additionalRemarks = 'Please enter remarks';
    }

    if (Object.keys(nextErrors).length > 0) {
      setApprovalErrors(nextErrors);
      return;
    }

    setApprovalSubmitError('');
    setIsApprovalSubmitting(true);

    try {
      const nextApprovalStatus = 'Approved';
      const response = await updateClaimApprovalStatus({
        claimID: selectedApprovalClaim.claimID,
        approvalStatus: nextApprovalStatus,
        reimbursementPercentage: approvalForm.reimbursementPercentage,
        remarks: approvalForm.additionalRemarks
      });

      if (!response || (response.success !== true && response.success !== 'true')) {
        throw new Error(response?.msg || 'Failed to update claim approval status.');
      }

      setClaims((prev) => prev.map((claim) => (
        claim.id === selectedApprovalClaim?.id
          ? {
            ...claim,
            status: nextApprovalStatus,
            approvalStatus: nextApprovalStatus,
            reimbursementPercentage: approvalForm.reimbursementPercentage,
            additionalRemarks: approvalForm.additionalRemarks,
            remarks: approvalForm.additionalRemarks
          }
          : claim
      )));

      handleCloseApprovalDialog();
      setClaimListRefreshKey((prev) => prev + 1);
      await showSuccessAlert(response?.msg || 'Expense claim approval captured successfully!', 'Success');
    } catch (error) {
      setApprovalSubmitError(error?.message || 'Failed to update claim approval status.');
    } finally {
      setIsApprovalSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      showAlert('Please fill all mandatory fields correctly.', 'Validation');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await insertTourExpenseClaim(formData);

      if (!response || (response.success !== true && response.success !== 'true')) {
        throw new Error(response?.msg || 'Failed to submit Tour Expense Claim.');
      }

      setIsSubmitting(false);
      await showSuccessAlert(response?.msg || 'Tour Expense Claim submitted successfully!', 'Success');
      handleReset();
      setShowCreateForm(false);
    } catch (error) {
      showAlert(error?.message || 'Failed to submit Tour Expense Claim.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        minHeight: '100%',
        padding: '0 12px 28px'
      }}
    >
      {isSubmitting ? (
        <div className="overlay">
          <Loader />
          <span style={{ marginLeft: '10px' }}>Submitting Tour Expense Claim...</span>
        </div>
      ) : null}

      {isApprovalSubmitting ? (
        <div className="overlay">
          <Loader />
          <span style={{ marginLeft: '10px' }}>Submitting approval...</span>
        </div>
      ) : null}

      <div className="cutrange" style={{ width: '100%', maxWidth: '100%' }}>
        <span className="font20">TOUR EXPENSE CLAIM</span>
      </div>

      <Card
        sx={{
          width: '100%',
          maxWidth: '100%',
          p: { xs: 1.5, md: 3, xl: 4 },
          borderRadius: 2.5
        }}
      >
        {!showCreateForm ? (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h5">{isApprovalUser ? 'Approval Pending' : 'Tour Expense Claim List'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {isApprovalUser
                    ? `Pending expense approvals with remarks for logged in code ${loggedInUserCode || '-'}.`
                    : 'Approved claims are shown here. Use the button to create a new claim.'}
                </Typography>
              </Box>
              {isAgentUser ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateForm(true)}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    alignSelf: { xs: 'flex-end', sm: 'center' },
                    width: 'fit-content',
                    minWidth: 150,
                    ml: { xs: 'auto', sm: 0 },
                    whiteSpace: 'nowrap',
                    px: 1.75,
                    py: 0.75,
                    flexShrink: 0
                  }}
                >
                  Create Claim
                </Button>
              ) : null}
            </Stack>

            {(isAgentUser || isApprovalUser) ? (
              <Card variant="outlined" sx={{ borderRadius: 2.5, mb: 2.5 }}>
                <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    {(isAgentUser || isSOUser) ? (
                      <Grid item xs={12} md={6} lg={4} xl={3} sx={{ pt: '8px !important' }}>
                        <Box sx={searchableFieldWrapperSx}>
                          <Typography variant="body2" sx={searchableFieldLabelSx}>
                            Salesperson
                          </Typography>
                          <Select
                            value={resolvedAgentFilterSalespersonOption}
                            placeholder="Search Salesperson Name or Code"
                            isSearchable
                            isClearable
                            options={salespersonCodeOptions}
                            styles={searchableDropdownStyles}
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            noOptionsMessage={getSearchableNoOptionsMessage}
                            onInputChange={(inputValue, actionMeta) => {
                              if (actionMeta.action === 'input-change') {
                                setSalespersonSearchText(inputValue);
                              }
                              if (actionMeta.action === 'menu-close' && !inputValue) {
                                setSalespersonSearchText('');
                              }
                            }}
                            onChange={(selectedOption) => handleAgentFilterSelectChange('salespersonCode', selectedOption)}
                          />
                        </Box>
                      </Grid>
                    ) : null}
                    {(isAgentUser || isSPUser || isPrimUser) ? (
                      <Grid item xs={12} md={6} lg={4} xl={3} sx={{ pt: '8px !important' }}>
                        <Box sx={searchableFieldWrapperSx}>
                          <Typography variant="body2" sx={searchableFieldLabelSx}>
                            SalesPerson L1
                          </Typography>
                          <Select
                            value={resolvedAgentFilterSalespersonL1Option}
                            placeholder="Search SalesPerson L1 Name or Code"
                            isSearchable
                            isClearable
                            options={salespersonL1Options}
                            styles={searchableDropdownStyles}
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            noOptionsMessage={getSearchableNoOptionsMessage}
                            onInputChange={(inputValue, actionMeta) => {
                              if (actionMeta.action === 'input-change') {
                                setSalespersonL1SearchText(inputValue);
                              }
                              if (actionMeta.action === 'menu-close' && !inputValue) {
                                setSalespersonL1SearchText('');
                              }
                            }}
                            onChange={(selectedOption) => handleAgentFilterSelectChange('salespersonL1Code', selectedOption)}
                          />
                        </Box>
                      </Grid>
                    ) : null}
                    <Grid item xs={12} md={6} lg={4} xl={3} sx={{ pt: '8px !important' }}>
                      <Box sx={searchableFieldWrapperSx}>
                        <Typography variant="body2" sx={searchableFieldLabelSx}>
                          Traveling Agency
                        </Typography>
                        <Select
                          value={resolvedAgentFilterTravelAgencyOption}
                          placeholder="Search Traveling Agency Name or Code"
                          isSearchable
                          isClearable
                          options={travelAgencyOptions}
                          styles={searchableDropdownStyles}
                          isLoading={travelAgencyLoading}
                          menuPortalTarget={selectMenuPortalTarget}
                          menuPosition="fixed"
                          noOptionsMessage={getSearchableNoOptionsMessage}
                          onInputChange={(inputValue, actionMeta) => {
                            if (actionMeta.action === 'input-change') {
                              setTravelAgencySearchText(inputValue);
                            }
                            if (actionMeta.action === 'menu-close' && !inputValue) {
                              setTravelAgencySearchText('');
                            }
                          }}
                          onChange={(selectedOption) => handleAgentFilterSelectChange('travelingAgency', selectedOption)}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} xl={3}>
                      <TextField
                        select
                        fullWidth
                        label="Status"
                        name="status"
                        value={agentFilters.status}
                        onChange={handleAgentFilterChange}
                        {...selectFieldProps}
                      >
                        {statusFilterOptions.map((option) => (
                          <MenuItem key={option.value || 'all-status'} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} xl={3}>
                      <TextField
                        fullWidth
                        label="HR One Tour Program Number"
                        name="hrOneReferenceNumber"
                        value={agentFilters.hrOneReferenceNumber}
                        onChange={handleAgentFilterChange}
                        placeholder="HR-TP-001"
                        {...textFieldProps}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} xl={3}>
                      <TextField
                        fullWidth
                        label="From Date"
                        name="fromDate"
                        type="date"
                        value={agentFilters.fromDate}
                        onChange={handleAgentFilterChange}
                        {...textFieldProps}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} xl={3}>
                      <TextField
                        fullWidth
                        label="To Date"
                        name="toDate"
                        type="date"
                        value={agentFilters.toDate}
                        onChange={handleAgentFilterChange}
                        {...textFieldProps}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} xl={3}>
                      <Button fullWidth variant="outlined" onClick={handleResetAgentFilters} sx={{ minHeight: 40, textTransform: 'none' }}>
                        Reset Filters
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : null}

            <Card variant="outlined" sx={{ borderRadius: 2.5, position: 'relative' }}>
              <CardContent sx={{ p: 0 }}>
                {isTableLoading ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.7)'
                    }}
                  >
                    <Loader />
                  </Box>
                ) : null}
                <TableContainer
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'visible'
                  }}
                >
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      width: '100%',
                      tableLayout: 'auto',
                      '& .MuiTableCell-root': {
                        borderBottomColor: 'rgba(224, 224, 224, 0.8)'
                      }
                    }}
                  >
                    <TableHead>
                      {isApprovalUser ? (
                        <TableRow>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx }}>Claim No.</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>
                            {isSPUser ? 'SalesPerson L1' : 'SalesPerson'}
                          </TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx }}>Traveling Agency</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>HR Code</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Amt</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Reimbursed %</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...remarksColumnSx }}>Additional Remarks</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Status</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Created At</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Action</TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx }}>Claim No.</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>SalesPerson</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>SalesPerson L1</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx }}>Traveling Agency</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>HR Code</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Amt</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Reimbursed %</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...remarksColumnSx }}>Additional Remarks</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Status</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Created At</TableCell>
                          <TableCell sx={{ ...compactHeaderCellSx, ...equalCellSx, ...centeredCellSx }}>Action</TableCell>
                        </TableRow>
                      )}
                    </TableHead>
                    <TableBody>
                      {isTableLoading ? (
                        <TableRow>
                          <TableCell
                            colSpan={isApprovalUser ? 10 : 11}
                            sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}
                          >
                            Loading data...
                          </TableCell>
                        </TableRow>
                      ) : (isAgentUser ? filteredAgentClaims : visibleClaims).length > 0 ? (isAgentUser ? filteredAgentClaims : visibleClaims).map((claim) => (
                        isApprovalUser ? (
                          <TableRow key={`${claim.id}-pending`} hover>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx }}>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handleOpenClaimDetails(claim)}
                                sx={{
                                  textTransform: 'none',
                                  minWidth: 'auto',
                                  p: 0,
                                  justifyContent: 'flex-start',
                                  color: '#1976d2',
                                  textDecoration: 'underline',
                                  fontWeight: 600
                                }}
                              >
                                {claim.id}
                              </Button>
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {isSPUser
                                ? (claim.salesOfficerName || claim.salespersonL1Code || '-')
                                : (claim.salespersonName || claim.salespersonCode || '-')}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {claim.travelAgencyName || claim.travelingAgency || '-'}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>{claim.hrOneReferenceNumber || '-'}</TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>{claim.amount}</TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>{claim.reimbursementPercentage ? `${claim.reimbursementPercentage}%` : '-'}</TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...remarksColumnSx, whiteSpace: 'normal' }}>
                              {claim.additionalRemarks || '-'}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.72rem',
                                  ...getStatusTextSx(claim.status)
                                }}
                              >
                                {claim.status}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>
                              {formatDisplayDate(claim.createdAt)}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>
                              {isApprovalUser ? (
                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenClaimHistory(claim)}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      border: '1px solid rgba(25, 118, 210, 0.35)',
                                      color: '#1976d2',
                                      backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.12)'
                                      }
                                    }}
                                    title="History"
                                  >
                                    <HistoryOutlinedIcon fontSize="small" />
                                  </IconButton>
                                  {isApprovalActionDisabled(claim, { isSPUser, isSOUser }) ? (
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 75,
                                        minHeight: 30,
                                        px: 1,
                                        borderRadius: 1,
                                        color: 'rgba(0, 0, 0, 0.45)',
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                        border: '1px solid rgba(0, 0, 0, 0.12)',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        cursor: 'not-allowed'
                                      }}
                                    >
                                      {String(claim.status || '').trim().toLowerCase() === 'approved' ? 'Approved' : 'Approve'}
                                    </Box>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleOpenApprovalDialog(claim)}
                                      sx={{
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      Approve
                                    </Button>
                                  )}
                                </Stack>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow key={claim.id} hover>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx }}>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handleOpenClaimDetails(claim)}
                                sx={{
                                  textTransform: 'none',
                                  minWidth: 'auto',
                                  p: 0,
                                  justifyContent: 'flex-start',
                                  color: '#1976d2',
                                  textDecoration: 'underline',
                                  fontWeight: 600
                                }}
                              >
                                {claim.id}
                              </Button>
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {claim.salespersonName || claim.salespersonCode || '-'}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {claim.salesOfficerName || claim.salespersonL1Code || '-'}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {claim.travelAgencyName || claim.travelingAgency || '-'}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>{claim.hrOneReferenceNumber || '-'}</TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>{claim.amount}</TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>{claim.reimbursementPercentage ? `${claim.reimbursementPercentage}%` : '-'}</TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...remarksColumnSx, whiteSpace: 'normal' }}>
                              {claim.additionalRemarks || '-'}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.72rem',
                                  ...getStatusTextSx(claim.status)
                                }}
                              >
                                {claim.status}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>
                              {formatDisplayDate(claim.createdAt)}
                            </TableCell>
                            <TableCell sx={{ ...compactBodyCellSx, ...equalCellSx, ...centeredCellSx }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenClaimHistory(claim)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  border: '1px solid rgba(25, 118, 210, 0.35)',
                                  color: '#1976d2',
                                  backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.12)'
                                  }
                                }}
                                title="History"
                              >
                                <HistoryOutlinedIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )
                      )) : (
                        <TableRow>
                          <TableCell
                            colSpan={isApprovalUser ? 10 : 11}
                            sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}
                          >
                            {isApprovalUser
                              ? 'No approval pending claims found for this login.'
                              : 'No data found'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        ) : (
          isAgentUser ? (
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <Stack spacing={0.5} sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr auto' },
                    alignItems: 'center',
                    minHeight: 32,
                    rowGap: 0.2,
                    columnGap: 2
                  }}
                >
                  <Button
                    variant="text"
                    onClick={() => {
                      handleReset();
                      setShowCreateForm(false);
                    }}
                    startIcon={<ArrowBackIcon fontSize="small" />}
                    sx={{
                      textTransform: 'none',
                      px: 0,
                      minWidth: 'auto',
                      justifySelf: 'start'
                    }}
                  >
                    Back To List
                  </Button>
                  <Typography variant="h5" sx={{ textAlign: 'center', gridColumn: { xs: '1', sm: '2' } }}>
                    Create Tour Expense Claim
                  </Typography>
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
                </Box>
              </Stack>

              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={{ xs: 2, md: 2.5, xl: 3 }}>
                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="From Date"
                    name="fromDate"
                    type="date"
                    value={formData.fromDate}
                    onChange={handleTextChange}
                    error={Boolean(errors.fromDate)}
                    helperText={errors.fromDate}
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="To Date"
                    name="toDate"
                    type="date"
                    value={formData.toDate}
                    onChange={handleTextChange}
                    error={Boolean(errors.toDate)}
                    helperText={errors.toDate}
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <Box sx={searchableFieldWrapperSx}>
                    <Typography variant="body2" sx={searchableFieldLabelSx}>
                      Salesperson <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <Select
                      value={resolvedSalespersonOption}
                      placeholder="Search Salesperson Name or Code"
                      isSearchable
                      isClearable
                      options={salespersonCodeOptions}
                      styles={searchableDropdownStyles}
                      menuPortalTarget={selectMenuPortalTarget}
                      menuPosition="fixed"
                      noOptionsMessage={getSearchableNoOptionsMessage}
                      onInputChange={(inputValue, actionMeta) => {
                        if (actionMeta.action === 'input-change') {
                          setSalespersonSearchText(inputValue);
                        }
                        if (actionMeta.action === 'menu-close' && !inputValue) {
                          setSalespersonSearchText('');
                        }
                      }}
                      onChange={handleSalespersonChange}
                    />
                  </Box>
                  {errors.salespersonCode ? (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, ml: 1.75 }}>
                      {errors.salespersonCode}
                    </Typography>
                  ) : null}
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Mode of Transport"
                    name="modeOfTransport"
                    value={formData.modeOfTransport}
                    onChange={handleTextChange}
                    error={Boolean(errors.modeOfTransport)}
                    helperText={errors.modeOfTransport}
                    placeholder="Taxi"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Vehicle"
                    name="vehicleNumber"
                    value={formData.vehicleNumber}
                    onChange={handleTextChange}
                    inputProps={{ maxLength: 20 }}
                    error={Boolean(errors.vehicleNumber)}
                    helperText={errors.vehicleNumber}
                    placeholder="Enter vehicle number"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Total KM Travelled"
                    name="totalKilometersTravelled"
                    value={formData.totalKilometersTravelled}
                    onChange={handleNumberChange}
                    error={Boolean(errors.totalKilometersTravelled)}
                    helperText={errors.totalKilometersTravelled}
                    placeholder="250"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="From Station"
                    name="fromStation"
                    value={formData.fromStation}
                    onChange={handleTextChange}
                    error={Boolean(errors.fromStation)}
                    helperText={errors.fromStation}
                    placeholder="Mumbai"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    fullWidth
                    label="Mid Stations"
                    name="midStations"
                    value={formData.midStations}
                    onChange={handleTextChange}
                    helperText="Optional"
                    placeholder="Pune"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="To Station"
                    name="toStation"
                    value={formData.toStation}
                    onChange={handleTextChange}
                    error={Boolean(errors.toStation)}
                    helperText={errors.toStation}
                    placeholder="Nashik"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Rate per KM"
                    name="ratePerKilometer"
                    value={formData.ratePerKilometer}
                    onChange={handleNumberChange}
                    error={Boolean(errors.ratePerKilometer)}
                    helperText={errors.ratePerKilometer}
                    placeholder="10"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Total Amount"
                    name="totalAmount"
                    value={formData.totalAmount}
                    InputProps={{ readOnly: true }}
                    error={Boolean(errors.totalAmount)}
                    helperText={errors.totalAmount || 'Auto calculated'}
                    placeholder="2500"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <Box sx={searchableFieldWrapperSx}>
                    <Typography variant="body2" sx={searchableFieldLabelSx}>
                      SalesPerson L1
                    </Typography>
                    <Select
                      value={resolvedSalespersonL1Option}
                      placeholder="Search SalesPerson L1 Name or Code"
                      isSearchable
                      isClearable
                      options={salespersonL1Options}
                      styles={searchableDropdownStyles}
                      menuPortalTarget={selectMenuPortalTarget}
                      menuPosition="fixed"
                      noOptionsMessage={getSearchableNoOptionsMessage}
                      onInputChange={(inputValue, actionMeta) => {
                        if (actionMeta.action === 'input-change') {
                          setSalespersonL1SearchText(inputValue);
                        }
                        if (actionMeta.action === 'menu-close' && !inputValue) {
                          setSalespersonL1SearchText('');
                        }
                      }}
                      onChange={handleSalespersonL1Change}
                    />
                  </Box>
                  {errors.salespersonL1Code ? (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, ml: 1.75 }}>
                      {errors.salespersonL1Code}
                    </Typography>
                  ) : null}
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <Box sx={searchableFieldWrapperSx}>
                    <Typography variant="body2" sx={searchableFieldLabelSx}>
                      Traveling Agency <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <Select
                      value={selectedTravelAgencyDetails}
                      placeholder="Search Traveling Agency Name or Code"
                      isSearchable
                      isClearable
                      options={travelAgencyOptions}
                      styles={searchableDropdownStyles}
                      isLoading={travelAgencyLoading}
                      menuPortalTarget={selectMenuPortalTarget}
                      menuPosition="fixed"
                      noOptionsMessage={getSearchableNoOptionsMessage}
                      onInputChange={(inputValue, actionMeta) => {
                        if (actionMeta.action === 'input-change') {
                          setTravelAgencySearchText(inputValue);
                        }
                        if (actionMeta.action === 'menu-close' && !inputValue) {
                          setTravelAgencySearchText('');
                        }
                      }}
                      onChange={handleTravelAgencyChange}
                    />
                  </Box>
                  {errors.travelingAgency ? (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, ml: 1.75 }}>
                      {errors.travelingAgency}
                    </Typography>
                  ) : null}
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Bank Name"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleTextChange}
                    error={Boolean(errors.bankName)}
                    helperText={errors.bankName}
                    placeholder="HDFC Bank"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Branch Name"
                    name="branchName"
                    value={formData.branchName}
                    onChange={handleTextChange}
                    error={Boolean(errors.branchName)}
                    helperText={errors.branchName}
                    placeholder="Mumbai Main Branch"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Account Holder Name"
                    name="accountHolderName"
                    value={formData.accountHolderName}
                    onChange={handleTextChange}
                    error={Boolean(errors.accountHolderName)}
                    helperText={errors.accountHolderName}
                    placeholder="Enter account holder name"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="Bank Account Number"
                    name="bankAccountNumber"
                    value={formData.bankAccountNumber}
                    onChange={handleTextChange}
                    inputProps={{ maxLength: 18, inputMode: 'numeric' }}
                    error={Boolean(errors.bankAccountNumber)}
                    helperText={errors.bankAccountNumber}
                    placeholder="123456789012"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3}>
                  <TextField
                    required
                    fullWidth
                    label="IFSC Code"
                    name="ifscCode"
                    value={formData.ifscCode}
                    onChange={handleTextChange}
                    error={Boolean(errors.ifscCode)}
                    helperText={errors.ifscCode}
                    placeholder="HDFC0001234"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={4} xl={4}>
                  <TextField
                    required
                    fullWidth
                    label="HR One Tour Program Number"
                    name="hrOneReferenceNumber"
                    value={formData.hrOneReferenceNumber}
                    onChange={handleTextChange}
                    error={Boolean(errors.hrOneReferenceNumber)}
                    helperText={errors.hrOneReferenceNumber}
                    placeholder="HR-TP-001"
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={4} xl={4}>
                  <TextField
                    required
                    fullWidth
                    label="Full Tour From Date"
                    name="fullTourFromDate"
                    type="date"
                    value={formData.fullTourFromDate}
                    onChange={handleTextChange}
                    error={Boolean(errors.fullTourFromDate)}
                    helperText={errors.fullTourFromDate}
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={4} xl={4}>
                  <TextField
                    required
                    fullWidth
                    label="Full Tour To Date"
                    name="fullTourToDate"
                    type="date"
                    value={formData.fullTourToDate}
                    onChange={handleTextChange}
                    error={Boolean(errors.fullTourToDate)}
                    helperText={errors.fullTourToDate}
                    {...textFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={8} xl={9}>
                  <Typography variant="body2" sx={{ mb: 0.75, color: 'rgba(0, 0, 0, 0.6)' }}>
                    Invoice File <span style={{ color: '#d32f2f' }}>*</span>
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 2,
                      flexWrap: { xs: 'wrap', md: 'nowrap' }
                    }}
                  >
                    <Button
                      component="label"
                      variant="contained"
                      sx={{
                        minWidth: 210,
                        height: 40,
                        px: 1,
                        borderRadius: 1,
                        textTransform: 'none',
                        boxShadow: 'none'
                      }}
                    >
                      Upload Invoice
                      <input
                        hidden
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,image/jpeg,image/jpg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        type="file"
                        onChange={handleFileChange}
                      />
                    </Button>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 40,
                        border: `1px solid ${errors.invoiceFile ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)'}`,
                        borderRadius: 1,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#fff',
                        color: invoiceFileName ? 'text.primary' : 'text.secondary'
                      }}
                    >
                      {invoiceFileName || 'No file selected'}
                    </Box>
                  </Box>
                  {errors.invoiceFile ? (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                      {errors.invoiceFile}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      {acceptedInvoiceFormatsLabel}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => {
                        handleReset();
                        setShowCreateForm(false);
                      }}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                      Submit Claim
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          ) : null
        )}
      </Card>

      <Dialog open={Boolean(selectedApprovalClaim)} onClose={handleCloseApprovalDialog} fullWidth maxWidth="md">
        <DialogTitle>Approve Expense Claim</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            {approvalSubmitError ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  color: '#d32f2f',
                  backgroundColor: '#fdecea',
                  border: '1px solid #f5c2c7',
                  fontSize: '0.875rem'
                }}
              >
                {approvalSubmitError}
              </Box>
            ) : null}
            <TextField
              fullWidth
              label="Claim No."
              value={selectedApprovalClaim?.id || ''}
              InputProps={{ readOnly: true }}
              {...textFieldProps}
            />
            <TextField
              select
              required
              fullWidth
              label="Enter the Percentage of amount to be Reimbursed"
              name="reimbursementPercentage"
              value={approvalForm.reimbursementPercentage}
              onChange={handleApprovalFieldChange}
              error={Boolean(approvalErrors.reimbursementPercentage)}
              helperText={approvalErrors.reimbursementPercentage}
              {...selectFieldProps}
            >
              <MenuItem value="">Select Reimbursement Percentage</MenuItem>
              {reimbursementOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              required
              multiline
              minRows={3}
              label="Remarks"
              name="additionalRemarks"
              value={approvalForm.additionalRemarks}
              onChange={handleApprovalFieldChange}
              inputProps={{ maxLength: 2000 }}
              error={Boolean(approvalErrors.additionalRemarks)}
              helperText={approvalErrors.additionalRemarks || ''}
              placeholder="Enter approval remarks"
              {...textFieldProps}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseApprovalDialog} sx={{ textTransform: 'none' }} disabled={isApprovalSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApprovalSubmit} sx={{ textTransform: 'none' }} disabled={isApprovalSubmitting}>
            Submit Approval
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedClaimDetails)} onClose={handleCloseClaimDetails} fullWidth maxWidth="lg">
        <DialogTitle>Claim Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Claim No." value={selectedClaimDetails?.id || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Document No." value={selectedClaimDetails?.documentNo || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="From Date" value={formatDialogDate(selectedClaimDetails?.fromDate)} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="To Date" value={formatDialogDate(selectedClaimDetails?.toDate)} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Salesperson" value={formatNameCodeDisplay(selectedClaimDetails?.salespersonName, selectedClaimDetails?.salespersonCode)} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Mode of Transport" value={selectedClaimDetails?.modeOfTransport || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Vehicle / Flight Number" value={selectedClaimDetails?.vehicleNumber || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Total KM Travelled" value={selectedClaimDetails?.totalKilometersTravelled || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="From Station" value={selectedClaimDetails?.fromStation || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Mid Stations" value={selectedClaimDetails?.midStations || '-'} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="To Station" value={selectedClaimDetails?.toStation || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Rate per KM" value={selectedClaimDetails?.ratePerKilometer || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Amount" value={selectedClaimDetails?.amount || selectedClaimDetails?.totalAmount || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="SalesPerson L1" value={formatNameCodeDisplay(selectedClaimDetails?.salesOfficerName, selectedClaimDetails?.salespersonL1Code)} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Traveling Agency" value={formatNameCodeDisplay(selectedClaimDetails?.travelAgencyName, selectedClaimDetails?.travelingAgency)} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Bank Name" value={selectedClaimDetails?.bankName || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Branch Name" value={selectedClaimDetails?.branchName || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Account Holder Name" value={selectedClaimDetails?.accountHolderName || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Bank Account Number" value={selectedClaimDetails?.bankAccountNumber || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="IFSC Code" value={selectedClaimDetails?.ifscCode || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="HR Ref No." value={selectedClaimDetails?.hrOneReferenceNumber || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Full Tour From Date" value={formatDialogDate(selectedClaimDetails?.fullTourFromDate)} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Full Tour To Date" value={formatDialogDate(selectedClaimDetails?.fullTourToDate)} InputProps={{ readOnly: true }} {...textFieldProps} />
            </Grid>
            <Grid item xs={12} md={4}>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<DescriptionOutlinedIcon />}
                onClick={() => handleDownloadInvoice(selectedClaimDetails?.invoiceFileName)}
                disabled={!selectedClaimDetails?.invoiceFileName}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', minHeight: 40 }}
              >
                {selectedClaimDetails?.invoiceFileName || 'No file available'}
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={searchableFieldWrapperSx}>
                <Typography variant="body2" sx={searchableFieldLabelSx}>
                  Status
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 40,
                    px: 1.5,
                    borderRadius: '999px',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    ...getStatusTextSx(selectedClaimDetails?.status)
                  }}
                >
                  {selectedClaimDetails?.status || '-'}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseClaimDetails} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedHistoryClaim)} onClose={handleCloseClaimHistory} fullWidth maxWidth="lg">
        <DialogTitle>
          Claim History{selectedHistoryClaim?.id ? ` - ${selectedHistoryClaim.id}` : ''}
        </DialogTitle>
        <DialogContent>
          {claimHistoryLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              Loading history...
            </Typography>
          ) : claimHistoryError ? (
            <Typography variant="body2" color="error" sx={{ pt: 1 }}>
              {claimHistoryError}
            </Typography>
          ) : claimHistory.length > 0 ? (
            <TableContainer sx={{ pt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Action By</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reimbursed %</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {claimHistory.map((item) => (
                    <TableRow key={item.logID}>
                      <TableCell>{item.actionType || '-'}</TableCell>
                      <TableCell>{item.actionByUserName || item.actionByUserCode || '-'}</TableCell>
                      <TableCell>{item.actionByRole || '-'}</TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            fontSize: '0.72rem',
                            ...getStatusTextSx(item.claimStatus)
                          }}
                        >
                          {item.claimStatus || '-'}
                        </Box>
                      </TableCell>
                      <TableCell>{item.reimbursementPercentage ? `${item.reimbursementPercentage}%` : '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.remarks || '-'}</TableCell>
                      <TableCell>{formatHistoryDateTime(item.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              No history found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseClaimHistory} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TourExpenseClaim;
